import { useTranslation } from "next-i18next";

import { useFormatPrice } from "@/common/hooks/intl.hooks";
import { CreateAdV2SparkleIcon } from "@/features/create-ad-v2/components/CreateAdV2Icons";
import { getCreateAdV2PriceScalePercent } from "@/features/create-ad-v2/utils/create-ad-v2-price.utils";
import type { ValueCheckPriceRange } from "@/features/value-check/model";
import { Body } from "@/ui/typography/Body";
import { Flavor } from "@/ui/typography/Flavor";
import { Helper } from "@/ui/typography/Helper";

/**
 * The sold-price range in the style of create-ad v2's price step (the ticket asks for that): the
 * light green panel, the range as a band on one scale, and the three anchors with create-ad's own
 * labels. The heading says "sold for", never "you can sell it for" (legal, WHO-6534), and the
 * figures are realised sale prices (gateway `valueCheckPrice`). Type per Notion 09.1: label in
 * Flavour 2, values in Filson Pro, nothing under 14px (F15-F17); the layout stays (F33).
 */
export const ValueCheckPriceBlock = ({
  price,
}: {
  price: ValueCheckPriceRange;
}) => {
  const { t } = useTranslation();
  const { formatPrice } = useFormatPrice();
  const { low, expected, high, comparableSales } = price;

  // Sold prices, so the anchors name sales, never advice (WHO-6534 F2).
  const anchors = [
    {
      value: low,
      label: t("value-check:price-anchor-low", "lowest sales"),
    },
    {
      value: expected,
      label: t("value-check:price-anchor-middle", "middle price"),
    },
    {
      value: high,
      label: t("value-check:price-anchor-high", "highest sales"),
    },
  ];

  return (
    <div className="rounded bg-flavour-50 p-4" data-testid="value-check.price">
      <Flavor className="flex items-center gap-1.5 text-brand-600">
        <CreateAdV2SparkleIcon aria-hidden className="size-4 shrink-0" />
        {t("value-check:price-title", "Comparable items sold for")}
      </Flavor>
      <Body className="mt-1 font-title text-2xl font-bold tabular-nums">
        {t("value-check:price-range", "{{low}} to {{high}}", {
          low: formatPrice(low),
          high: formatPrice(high),
        })}
      </Body>
      {/* One scale from 0 to 1.5 × the top of the range, as on create-ad's price step. */}
      <div
        aria-hidden
        className="relative mb-1.5 mt-6 h-2 rounded-full bg-white"
      >
        <span
          className="absolute inset-y-0 rounded-full bg-brand-500"
          style={{
            left: `${getCreateAdV2PriceScalePercent(low, high)}%`,
            width: `${
              getCreateAdV2PriceScalePercent(high, high) -
              getCreateAdV2PriceScalePercent(low, high)
            }%`,
          }}
        />
        {anchors.map(anchor => (
          <span
            key={anchor.label}
            className="absolute -top-[3px] h-3.5 w-px bg-brand-600"
            style={{
              left: `${getCreateAdV2PriceScalePercent(anchor.value, high)}%`,
            }}
          />
        ))}
      </div>
      <ul className="mt-3.5 grid grid-cols-3 gap-2">
        {anchors.map(anchor => (
          <li
            key={anchor.label}
            className="flex min-w-0 flex-col rounded border border-grey-300 bg-white px-2.5 py-2"
          >
            <span className="block font-title text-base font-bold tabular-nums text-black">
              {formatPrice(anchor.value)}
            </span>
            <span className="block font-body text-sm text-grey-700">
              {anchor.label}
            </span>
          </li>
        ))}
      </ul>
      <Helper className="mt-2.5 block text-grey-800">
        {comparableSales
          ? t("value-check:price-basis", {
              defaultValue_one: "Based on {{count}} sale on Whoppah.",
              defaultValue_other: "Based on {{count}} sales on Whoppah.",
              count: comparableSales,
            })
          : null}{" "}
        {t(
          "value-check:price-disclaimer",
          "An indication, not a guarantee: the final price depends on the condition, your photos and buyer interest."
        )}
      </Helper>
    </div>
  );
};
